import { FC, useEffect, useState } from 'react'

import { useDebounceFn } from 'ahooks'
import { Button, Toast } from 'antd-mobile'
import { useAtomValue } from 'jotai'

import Icon from '@/components/Icon'
import { isSuccess } from '@/axios'
import { putIMFollow, putCancelFollow, getUserCheckIsFollowed } from '@/api'
import { EConversationType } from '@/types'
import { loginStateAtom, useUserAtom, useGlobalLoading } from '@/store'
import { cn, customJumpHref, handleClickFollowTrack, jumpMainLogin } from '@/utils'

export interface IFollowButtonProps {
  userId?: string
  className?: string
  chatClassName?: string
  showChat?: boolean
  nickname?: string
  avatar_url?: string
}

const FollowButton: FC<IFollowButtonProps> = (props) => {
  const { userId, className, chatClassName, showChat, nickname, avatar_url } = props
  const [isFollowed, setIsFollowed] = useState(false)
  const isLogin = useAtomValue(loginStateAtom)
  const { user } = useUserAtom()
  const isMe = String(userId) === String(user.id)
  const { setGlobalLoading } = useGlobalLoading()

  const openChat = async (id: string) => {
    if (!isLogin) {
      await new Promise<void>((resloveFn) => {
        Toast.show({
          position: 'top',
          content: 'Login to continue',
          afterClose: resloveFn,
          duration: 1000,
        })
      })
      return jumpMainLogin()
    }
    const conversation = {
      id: '',
      session_type: EConversationType.PRIVATE,
      session_id: id || '',
      session_name: nickname || '',
      session_avatar_url: avatar_url || '',
      last_message_type: 0,
      last_message_content: '',
      last_message_created_at: 0,
      last_message_sender_id: '',
      last_read_at: 0,
    }

    const encodeData = encodeURIComponent(JSON.stringify(conversation))
    customJumpHref(`/message?jumpNow=1&chat=${encodeData}`)
  }

  const handleGetUserCheckIsFollowed = async (user_id: string) => {
    if (!isLogin) {
      return
    }
    const resp = await getUserCheckIsFollowed({ user_id })

    if (isSuccess(resp)) {
      setIsFollowed(resp.data.is_followed)
    }
  }

  const { run: handleFollowUser } = useDebounceFn(
    async () => {
      try {
        setGlobalLoading(true)
        handleClickFollowTrack(user.id, userId || '', '3')
        const resp = await putIMFollow({ user_id: userId })

        if (isSuccess(resp)) {
          setIsFollowed(true)
          Toast.show({
            content: 'Success',
            position: 'top',
          })
        }
      } catch (error) {
        console.error(error)
      } finally {
        setGlobalLoading(false)
      }
    },
    { wait: 200 },
  )

  const { run: handleUnfollowUser } = useDebounceFn(
    async () => {
      try {
        setGlobalLoading(true)
        const resp = await putCancelFollow({ following_id: userId })

        if (isSuccess(resp)) {
          setIsFollowed(false)
          Toast.show({
            content: 'Cancel Success',
            position: 'top',
          })
        }
      } catch (error) {
        console.error(error)
      } finally {
        setGlobalLoading(false)
      }
    },
    { wait: 200 },
  )

  useEffect(() => {
    if (userId) {
      handleGetUserCheckIsFollowed(userId)
    }
  }, [userId])

  return (
    <>
      {userId && !isMe ? (
        <>
          <Button
            onClick={isFollowed ? handleUnfollowUser : handleFollowUser}
            className={`g-follow-btn-v2 py-0 ${isFollowed ? 'g-btn-v2-primary' : ''} ${className}`}
          >
            {isFollowed ? 'Following' : 'Follow'}
          </Button>

          {showChat ? (
            <Icon
              name='Chat'
              className={cn('cursor-pointer', chatClassName)}
              onClick={() => {
                openChat(userId)
              }}
            />
          ) : null}
        </>
      ) : null}
    </>
  )
}

export default FollowButton
